/**
 * The shell (#1312, #1323).
 *
 * Three states, and the app has to be honest about which one it is in: not
 * signed in, signed in with no household yet, and running. Collapsing the
 * middle one into an error is how a first-time user gets told something went
 * wrong when nothing did.
 *
 * ⚠️ The funnel redirects and the navigation have to coexist. Setup and the
 * first pass at tastes are genuinely a sequence, so they redirect. But once
 * somebody HAS answered, /taste is a place they are allowed to visit — the old
 * redirect fired on "unfinished" rather than "never started", which with a nav
 * bar would bounce somebody straight back out of the screen they just tapped.
 */
import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { session, toSignIn } from "./lib/api";
import { twoPlates } from "./api/twoPlates";
import { Nav } from "./components/Nav";
import Setup from "./pages/Setup";
import Taste from "./pages/Taste";
import Deck from "./pages/Deck";
import PlanPage from "./pages/PlanPage";
import Kitchen from "./pages/Kitchen";
import Audit from "./pages/Audit";
import Cook from "./pages/Cook";
import Import from "./pages/Import";
import Review from "./pages/Review";
import Cookbook from "./pages/Cookbook";
import LinkTaste from "./pages/LinkTaste";

/** A bookmark or an old link to the retired in-app sign-in screen. */
function LeaveToLanding() {
  useEffect(() => { toSignIn("Sign in to open your kitchen."); }, []);
  return <Loading label="Taking you to sign in…" />;
}

function Loading({ label }: { label: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center px-6">
      <p className="text-muted text-sm">{label}</p>
    </div>
  );
}

/** Content, room for the bar, and the bar. */
function Chrome({ children }: { children: React.ReactNode }) {
  return (
    <>
      {/* pb-24 reserves the bar's height, so the last row of a shopping list is
          never trapped underneath it. */}
      <div className="mx-auto max-w-md px-5 pb-24 pt-8">{children}</div>
      <Nav />
    </>
  );
}

/**
 * First run only. Somebody with no household has nowhere to go but setup, and
 * somebody who has never answered a food card has an empty deck.
 */
function Gate({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useQuery({ queryKey: ["household"], queryFn: twoPlates.household });
  const location = useLocation();

  if (isLoading) return <Loading label="Loading your kitchen…" />;
  if (!data?.household) {
    return location.pathname === "/setup" ? <>{children}</> : <Navigate to="/setup" replace />;
  }

  // NEVER started, not "has not finished". A half-answered profile is a normal
  // state the engine handles — silence reads as "will eat" — and forcing
  // somebody back into the deck to finish it would make the nav a trap.
  const neverAnswered = data.household.eaters.every((e) => Object.keys(e.answers).length === 0);
  if (neverAnswered && location.pathname !== "/taste") return <Navigate to="/taste" replace />;

  return <>{children}</>;
}

/**
 * ⛔ Signing in happens on the LANDING PAGE, not in here.
 *
 * There is one sign-in surface — the modal on / — so anybody without a session
 * leaves the app entirely rather than meeting a second login screen with
 * different styling. api.ts does the same on a 401 it cannot refresh.
 */
function RequireSession({ children }: { children: React.ReactNode }) {
  const signedIn = session.signedIn();

  useEffect(() => {
    if (!signedIn) toSignIn("Sign in to open your kitchen.");
  }, [signedIn]);

  if (!signedIn) return <Loading label="Taking you to sign in…" />;
  return <>{children}</>;
}

/** Signed in, past the funnel, inside the chrome. */
function Page({ children }: { children: React.ReactNode }) {
  return (
    <RequireSession>
      <Gate>
        <Chrome>{children}</Chrome>
      </Gate>
    </RequireSession>
  );
}

export default function App() {
  return (
    <Routes>
      {/* No session, no chrome, nothing about the household (#1285). */}
      <Route path="/a/:token" element={<LinkTaste />} />

      {/* Genuinely a sequence, so no bar: there is nowhere else to be yet. */}
      <Route
        path="/setup"
        element={
          <RequireSession>
            <Gate>
              <Setup />
            </Gate>
          </RequireSession>
        }
      />

      <Route path="/deck" element={<Page><Deck /></Page>} />
      <Route path="/plan" element={<Page><PlanPage /></Page>} />
      <Route path="/taste" element={<Page><Taste /></Page>} />
      <Route path="/kitchen" element={<Page><Kitchen /></Page>} />
      <Route path="/plans/:planId/audit" element={<Page><Audit /></Page>} />
      <Route path="/cook/:mealId" element={<Page><Cook /></Page>} />
      <Route path="/import" element={<Page><Import /></Page>} />
      <Route path="/review" element={<Page><Review /></Page>} />
      <Route path="/cookbook" element={<Page><Cookbook /></Page>} />

      {/* Anything still pointing at the old route goes to the one that exists. */}
      <Route path="/sign-in" element={<LeaveToLanding />} />
      <Route path="/" element={<Navigate to="/plan" replace />} />
      <Route path="*" element={<Navigate to="/plan" replace />} />
    </Routes>
  );
}
